/**
 * MediaItemSideBar React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


// React
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

// Material-UI
import {withStyles} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

// 3rd party
import _ from 'lodash';

// application components
import FileUploadPanel from "../base/FileUploadPanel";
import MediaPanel from "../base/MediaPanel";
import MediaDownloadButton from "../base/MediaDownloadButton";

// application functionality
import {openMediaOverlayForUrl} from "../../operations/view";
import {uploadMediaFile} from "../../operations/media";
import {getMediaType, toUserFriendlySecondsString, toUserFriendlyBytesString} from "../../utils";
import {patchServerUrl} from "../../config";


/**
 * MediaItemSideBar allows uploading and showing media files
 *
 * @author Jens Gruschel
 */
export class MediaItemSideBar extends React.Component {

    constructor(props) {
        super(props);
        const object = props.object || {};
        const fields = object.fields || {};
        this.state = {
            objectId: props.objectId,
            uploading: false,
            mediaInfoUrl: fields.url,
            mediaInfo: object.mediainfo
        };
    }

    componentDidUpdate(prevProps) {
        const {props} = this;
        const object = props.object || {};
        const prevObject = prevProps.object || {};
        if (props.objectId !== prevProps.objectId || !_.isEqual(object.mediainfo, prevObject.mediainfo)) {
            const fields = object.fields || {};
            this.setState({
                objectId: props.objectId,
                uploading: false,
                mediaInfoUrl: fields.url,
                mediaInfo: object.mediainfo
            });
        }
    }

    onDrop = (acceptedFiles, rejectedFiles) => {
        if (!this.props.readOnly && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            this.setState({uploading: true});
            this.props.uploadMediaFile(
                file,
                this.props.objectId,
                "url",
                (errorMessage, url, mediaInfo) => this.onFinish(url, mediaInfo)
            );
        }
    }

    onFinish(url, mediaInfo) {
        this.setState({
            uploading: false,
            mediaInfoUrl: url,
            mediaInfo: mediaInfo
        });
    }

    render() {

        const fields = (this.props.object && this.props.object.fields) || {};
        const originalUrl = fields.url;
        const mediaInfo = (this.state.mediaInfoUrl === originalUrl && this.state.mediaInfo) || {};
        const webFormatUrl = mediaInfo.webformat;
        const url = webFormatUrl || originalUrl;

        if (originalUrl) {

            let onClick = undefined;
            switch(getMediaType(url)) {

                case "video":
                case "image":
                    onClick = () => this.props.openMediaOverlayForUrl(url);
                    break;

                default:
                    onClick = undefined;
                    break;
            }

            return <>
                <div style={{cursor: "pointer"}} onClick={onClick}>
                    <MediaPanel url={url} showControls={true} playOnClick={false} otherFilesAsDownload={false} />
                </div>
                {<MediaDownloadButton url={patchServerUrl(originalUrl)} />}
                {this.renderMediaInfo(mediaInfo)}
            </>;
        }
        else {

            return <FileUploadPanel
                onDrop={this.onDrop}
                disabled={this.props.readOnly}
                busy={this.state.uploading}
            >
                Datei hier ablegen<br/><br/>oder klicken um<br/>Datei auszuwählen
            </FileUploadPanel>;
        }
    }

    renderMediaInfo(mediaInfo) {
        return <Table>
            <TableBody>
            {mediaInfo.duration && <TableRow>
                    <TableCell>Dauer</TableCell>
                    <TableCell align="right">{toUserFriendlySecondsString(mediaInfo.duration)}</TableCell>
                </TableRow>}
                {mediaInfo.width && mediaInfo.height && <TableRow>
                    <TableCell>Maße</TableCell>
                    <TableCell align="right">{mediaInfo.width + "\xa0×\xa0" + mediaInfo.height + "\xa0px"}</TableCell>
                </TableRow>}
                {mediaInfo.filesize && <TableRow>
                    <TableCell>Dateigröße</TableCell>
                    <TableCell align="right">{toUserFriendlyBytesString(mediaInfo.filesize)}</TableCell>
                </TableRow>}
            </TableBody>
        </Table>;
    }
}

MediaItemSideBar.propTypes = {
    place: PropTypes.string.isRequired,
    fieldSet: PropTypes.object.isRequired,
    objectType: PropTypes.string,
    objectId: PropTypes.string,
    object: PropTypes.object,
    readOnly: PropTypes.bool,
    onObjectValueChange: PropTypes.func.isRequired
};

const styles = {
    media: {
        width: 204,
        height: 204,
        backgroundColor: "#cccccc"
    }
};

export default connect(undefined, {openMediaOverlayForUrl, uploadMediaFile})(withStyles(styles)(MediaItemSideBar));